import { useNow } from '../../hooks/useNow';
import { deviceZone, formatOffset, utcOffsetMinutes, zoneCity, zonedHMS } from '../../lib/time';
import { useSettings, type ClockPosition } from '../../store/settings';

type Size = 'md' | 'lg' | 'xl';

/** Where the clock sits in its area: vertical (align-items) + horizontal (justify-content). */
const POSITION: Record<ClockPosition, string> = {
  'top-left': 'items-start justify-start',
  'top-center': 'items-start justify-center',
  'top-right': 'items-start justify-end',
  'middle-left': 'items-center justify-start',
  'middle-center': 'items-center justify-center',
  'middle-right': 'items-center justify-end',
  'bottom-left': 'items-end justify-start',
  'bottom-center': 'items-end justify-center',
  'bottom-right': 'items-end justify-end',
};

const ALIGN = {
  left: { text: 'text-left', items: 'items-start', row: 'justify-start' },
  center: { text: 'text-center', items: 'items-center', row: 'justify-center' },
  right: { text: 'text-right', items: 'items-end', row: 'justify-end' },
  // Justified: the date and other lines stretch to the width of the time, as one neat block.
  justify: { text: 'text-left [text-align-last:justify]', items: 'items-stretch', row: 'justify-between' },
} as const;

function safeFormat(opts: Intl.DateTimeFormatOptions, tz: string | undefined, now: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz || undefined }).format(now);
  } catch {
    // A time zone this browser doesn't know: fall back to local time.
    return new Intl.DateTimeFormat(undefined, opts).format(now);
  }
}

/** "London · UTC+1" for a zone ('' = this device), with an optional custom name. */
function placeLine(tz: string, label: string, now: Date, showPlace: boolean, showOffset: boolean): string {
  const zone = tz || deviceZone();
  const parts: string[] = [];
  if (showPlace) parts.push(label.trim() || zoneCity(zone));
  if (showOffset) {
    try {
      parts.push(formatOffset(utcOffsetMinutes(now, zone)));
    } catch {
      /* unknown zone: leave the offset out */
    }
  }
  return parts.join(' · ');
}

/**
 * Analogue face: hour ticks (or numbers), hands for the time in `tz`, and a second hand
 * when seconds are shown. Drawn in currentColor so it follows the theme.
 */
function Analogue({
  now,
  tz,
  numbers,
  seconds,
  className,
  place = 'xMidYMid',
}: {
  now: Date;
  tz: string;
  numbers: boolean;
  seconds: boolean;
  className: string;
  /** Where the face sits when its box isn't square (SVG preserveAspectRatio). */
  place?: 'xMinYMid' | 'xMidYMid' | 'xMaxYMid';
}) {
  let hms = { h: now.getHours(), m: now.getMinutes(), s: now.getSeconds() };
  try {
    hms = zonedHMS(now, tz || undefined);
  } catch {
    /* unknown zone: local time */
  }
  const minuteAngle = (hms.m + hms.s / 60) * 6;
  const hourAngle = ((hms.h % 12) + hms.m / 60) * 30;
  const hand = (angle: number, length: number, width: number, color = 'currentColor') => (
    <line x1="100" y1="100" x2="100" y2={100 - length} stroke={color} strokeWidth={width} strokeLinecap="round" transform={`rotate(${angle} 100 100)`} />
  );
  return (
    <svg viewBox="0 0 200 200" preserveAspectRatio={`${place} meet`} className={className} role="img" aria-label="Clock">
      <circle cx="100" cy="100" r="96" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.6" strokeWidth="3" />
      {Array.from({ length: 60 }, (_, i) => {
        const hour = i % 5 === 0;
        if (numbers && hour) return null;
        return (
          <line
            key={i}
            x1="100"
            y1={hour ? 10 : 8}
            x2="100"
            y2={hour ? 26 : 14}
            stroke="currentColor"
            strokeOpacity={hour ? 0.9 : 0.4}
            strokeWidth={hour ? 4 : 1.5}
            strokeLinecap="round"
            transform={`rotate(${i * 6} 100 100)`}
          />
        );
      })}
      {numbers &&
        Array.from({ length: 12 }, (_, i) => {
          const n = i + 1;
          const a = (n * 30 * Math.PI) / 180;
          return (
            <text
              key={n}
              x={100 + Math.sin(a) * 74}
              y={100 - Math.cos(a) * 74}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="22"
              fill="currentColor"
              className="font-heading"
            >
              {n}
            </text>
          );
        })}
      {hand(hourAngle, 48, 7)}
      {hand(minuteAngle, 72, 4.5)}
      {seconds && hand(hms.s * 6, 80, 1.8, 'var(--accent)')}
      <circle cx="100" cy="100" r="5" fill={seconds ? 'var(--accent)' : 'currentColor'} />
    </svg>
  );
}

const ANALOGUE_SIZE: Record<Size, string> = { md: 'h-28 w-28', lg: 'h-44 w-44', xl: 'h-60 w-60' };
const DIGITAL_SIZE: Record<Size, string> = { md: 'text-5xl', lg: 'text-7xl md:text-8xl', xl: 'text-8xl md:text-[10rem]' };

/**
 * The clock widget: digital or analogue, optional extra time zones, placed in its area by
 * Settings → Widgets → Clock. `fill`: it has a fixed-size box (Custom layout) – the panel
 * fills it and an analogue face grows to fit. `boxable={false}` where the layout already
 * draws the panel (Bento).
 */
export function Clock({ size = 'lg', boxable = true, fill = false }: { size?: Size; boxable?: boolean; fill?: boolean }) {
  const c = useSettings((s) => s.clock);
  const now = useNow(c.showSeconds ? 'second' : 'minute');
  const analogue = c.face !== 'digital';
  const align = ALIGN[c.align] ?? ALIGN.left;
  const extras = c.extraZones ?? [];
  // With several clocks, every one says where it is.
  const showPlace = c.showPlace || extras.length > 0;
  const showOffset = c.showOffset || extras.length > 0;

  const time = safeFormat({ hour: '2-digit', minute: '2-digit', second: c.showSeconds ? '2-digit' : undefined, hour12: c.hour12 }, c.timeZone, now);
  const date = safeFormat({ weekday: 'long', day: 'numeric', month: 'long' }, c.timeZone, now);
  const place = placeLine(c.timeZone, c.label ?? '', now, showPlace, showOffset);

  const face = analogue ? (
    fill ? (
      // In a fixed box: the face takes all the space the other lines leave, as large as fits.
      <div className="relative min-h-0 w-full flex-1">
        <Analogue
          now={now}
          tz={c.timeZone}
          numbers={c.face === 'analogue'}
          seconds={c.showSeconds}
          place={c.align === 'left' ? 'xMinYMid' : c.align === 'right' ? 'xMaxYMid' : 'xMidYMid'}
          className="absolute inset-0 h-full w-full"
        />
      </div>
    ) : (
      <Analogue now={now} tz={c.timeZone} numbers={c.face === 'analogue'} seconds={c.showSeconds} className={`${ANALOGUE_SIZE[size]} shrink-0`} />
    )
  ) : (
    <div className={`font-heading font-light leading-none tracking-tight tabular-nums ${DIGITAL_SIZE[size]}`}>{time}</div>
  );

  const block = (
    // An analogue face in a fixed box takes all the height the other lines leave.
    <div className={`text-shadow flex select-none flex-col ${align.items} ${align.text} ${fill ? (analogue ? 'h-full min-h-0 w-full' : 'max-h-full min-h-0') : ''}`}>
      {face}
      {c.showDate && <div className={`mt-2 font-normal opacity-90 ${size === 'md' ? 'text-lg' : 'text-xl md:text-2xl'}`}>{date}</div>}
      {place && <div className="muted mt-1 text-sm">{place}</div>}
      {extras.length > 0 && (
        <div className={`mt-4 flex flex-wrap gap-x-6 gap-y-3 ${align.row}`}>
          {extras.map((z) => (
            <MiniClock key={z.id} now={now} tz={z.timeZone} label={z.label} analogue={analogue} seconds={c.showSeconds} hour12={c.hour12} />
          ))}
        </div>
      )}
    </div>
  );

  const boxed = c.boxed && boxable;
  // The area: the clock is placed in it by the chosen position.
  return (
    <div className={`flex h-full w-full ${POSITION[c.position] ?? POSITION['bottom-left']} ${fill && boxed ? 'glass px-6 py-5' : ''}`}>
      {boxed && !fill ? <div className="glass px-6 py-5">{block}</div> : block}
    </div>
  );
}

/** One extra time zone: place, time and UTC offset (small analogue face when the main clock is analogue). */
function MiniClock({ now, tz, label, analogue, seconds, hour12 }: {
  now: Date;
  tz: string;
  label: string;
  analogue: boolean;
  seconds: boolean;
  hour12: boolean;
}) {
  const time = safeFormat({ hour: '2-digit', minute: '2-digit', hour12 }, tz, now);
  let offset = '';
  try {
    offset = formatOffset(utcOffsetMinutes(now, tz));
  } catch {
    /* unknown zone */
  }
  return (
    <div className="flex items-center gap-2 text-left [text-align-last:auto]">
      {/* Too small for numbers: tick marks only. */}
      {analogue && <Analogue now={now} tz={tz} numbers={false} seconds={seconds} className="h-14 w-14 shrink-0" />}
      <div>
        <div className="text-sm font-medium leading-tight">{label.trim() || zoneCity(tz)}</div>
        <div className="font-heading text-2xl font-light leading-tight tabular-nums">{time}</div>
        <div className="muted text-xs">{offset}</div>
      </div>
    </div>
  );
}
